/**
 * ScoutingIntelOverlay — Pitch Tipping Intelligence Overlay
 *
 * Full pipeline: upload → AI analysis → chat-over-video interface.
 * Combines video playback with timeline markers and an AI chat panel
 * where coaches can interrogate the pitch tipping findings.
 *
 * Pattern sources:
 *   PitchTippingAnalyzer  — MoE analysis via analyzeMedia()
 *   VideoChat             — chat interface with Gemini backend
 *   VideoViewerWithMarkers — timestamp parsing + seek
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Play,
  Pause,
  Send,
  Bot,
  User,
  Loader2,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Maximize2,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { C } from '../design-system/primitives/constants';
import { CAROLINA_BLUE } from '../../config/uncConstants';
import { NODE_BACKEND_URL } from '../../utils/constant';

marked.setOptions({ breaks: true, gfm: true });

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Sanitize markdown → HTML */
function safeMd(text) {
  if (!text) return '';
  return DOMPurify.sanitize(marked.parse(text));
}

/** Parse "MM:SS" → seconds */
function parseTimestamp(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/** Format seconds → "M:SS" */
function fmtTime(sec) {
  if (sec == null || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Extract structured indicators from analysis text */
function parseIndicators(analysisText) {
  if (!analysisText) return [];
  // Try JSON extraction first (ECHO agent format)
  try {
    const jsonMatch = analysisText.match(/\{[\s\S]*"indicators"[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed.indicators)) return parsed.indicators;
    }
  } catch { /* fall through to regex */ }

  // Fallback: extract timestamped observations from markdown
  const indicators = [];
  const lines = analysisText.split('\n');
  for (const line of lines) {
    const tsMatch = line.match(/\[(\d{1,2}:\d{2})\]/);
    if (tsMatch) {
      const clean = line.replace(/^[\s*\-#]+/, '').replace(/\[(\d{1,2}:\d{2})\]/, '').trim();
      indicators.push({
        timestamp: tsMatch[1],
        description: clean.slice(0, 120),
        category: 'OBSERVATION',
        confidence: 0.7,
      });
    }
  }
  return indicators;
}

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT BUILDER (pitch tipping context)
// ═══════════════════════════════════════════════════════════════════════════

function buildPitchTippingSystemPrompt(analysisText, meta = {}) {
  const { filename, playerName, context: ctx } = meta;
  return `You are ECHO — the Elite Competitive Hitter's Oracle — SLAM Sports AI's pitch-tipping intelligence system.

You have COMPLETE ACCESS to this analysis:

## VIDEO CONTEXT
- **File:** ${filename || 'Uploaded video'}
- **Player:** ${playerName || 'Unknown'}
- **Context:** ${ctx || 'Own Pitcher'}

## FULL ANALYSIS RESULTS
${analysisText || 'Analysis pending.'}

---

## YOUR ROLE
Answer coaching staff questions about this pitch tipping analysis. Be specific:
- Reference timestamps (e.g., [0:06]) so coaches can review in the video player
- Explain biomechanical tells in plain language
- Provide actionable scouting intelligence
- When asked about exploitation, give concrete at-bat strategies
- Reference the 7 ECHO categories (GLOVE, HEAD, SHOULDERS, ARM, LOWER_HALF, RHYTHM, RELEASE) when relevant

Be concise but thorough. Coaches need actionable intel, not academic papers.`;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS ANIMATION (analyzing phase)
// ═══════════════════════════════════════════════════════════════════════════

function AnalyzingPhase({ progress, currentExpert }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      {/* Pulsing ring */}
      <div className="relative w-28 h-28 mb-8">
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: `${CAROLINA_BLUE}15`, animationDuration: '2s' }}
        />
        <div
          className="absolute inset-2 rounded-full"
          style={{ background: C.s2, border: `2px solid ${CAROLINA_BLUE}40` }}
        />
        <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44" fill="none" stroke={C.border} strokeWidth="3" />
          <circle
            cx="50" cy="50" r="44" fill="none" stroke={CAROLINA_BLUE} strokeWidth="3"
            strokeDasharray={`${progress * 2.76} 276`}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold" style={{ color: CAROLINA_BLUE }}>
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {currentExpert && (
        <div
          className="rounded-xl px-5 py-3 mb-4 text-center transition-all duration-500"
          style={{ background: C.s2, border: `1px solid ${C.border}` }}
        >
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: C.t3 }}>
            Active Expert
          </p>
          <p className="text-sm font-semibold" style={{ color: CAROLINA_BLUE }}>
            {currentExpert.role || currentExpert.name}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: C.t2 }}>
            Weight: {((currentExpert.weight || 0) * 100).toFixed(0)}%
          </p>
        </div>
      )}

      <p className="text-sm font-medium" style={{ color: C.text }}>
        Running MoE Pitch Tipping Analysis...
      </p>
      <p className="text-xs mt-1" style={{ color: C.t3 }}>
        6 expert personas analyzing video simultaneously
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VIDEO PLAYER with timeline markers
// ═══════════════════════════════════════════════════════════════════════════

function VideoPlayer({ videoUrl, indicators, onTimestampClick }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime);
    const onMeta = () => setDuration(v.duration);
    const onEnd = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('ended', onEnd);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  }, []);

  const seekTo = useCallback((seconds) => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = seconds;
      setCurrentTime(seconds);
    }
  }, []);

  const seekRelative = useCallback((delta) => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + delta));
    }
  }, []);

  const handleTimelineClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    seekTo(pct * duration);
  }, [duration, seekTo]);

  // Expose seekTo for parent via ref callback
  useEffect(() => {
    if (onTimestampClick) onTimestampClick.current = seekTo;
  }, [seekTo, onTimestampClick]);

  const markers = useMemo(() => {
    if (!indicators || duration === 0) return [];
    return indicators
      .map((ind) => {
        const sec = parseTimestamp(ind.timestamp);
        if (sec == null || sec > duration) return null;
        return { ...ind, seconds: sec, pct: (sec / duration) * 100 };
      })
      .filter(Boolean);
  }, [indicators, duration]);

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#000' }}>
      <div className="relative aspect-video bg-black cursor-pointer" onClick={togglePlay}>
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          muted={muted}
          playsInline
        />
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: `${CAROLINA_BLUE}CC` }}
            >
              <Play size={24} fill="#000" color="#000" className="ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Timeline + markers */}
      <div className="px-3 pt-2">
        <div
          className="relative h-2 rounded-full cursor-pointer group"
          style={{ background: C.s3 }}
          onClick={handleTimelineClick}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-100"
            style={{
              width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
              background: CAROLINA_BLUE,
            }}
          />
          {markers.map((m, i) => (
            <div
              key={i}
              className="absolute top-1/2 w-2.5 h-2.5 rounded-full cursor-pointer transition-transform hover:scale-150 z-10"
              style={{
                left: `${m.pct}%`,
                background: m.severity === 'HIGH' ? '#FF4444' : m.severity === 'MEDIUM' ? '#F59E0B' : CAROLINA_BLUE,
                border: '2px solid #000',
                transform: 'translate(-50%, -50%)',
              }}
              title={`${m.timestamp} — ${m.description || m.tell || ''}`}
              onClick={(e) => { e.stopPropagation(); seekTo(m.seconds); }}
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <button onClick={() => seekRelative(-5)} className="p-1 rounded hover:bg-white/10">
            <SkipBack size={14} style={{ color: C.t2 }} />
          </button>
          <button onClick={togglePlay} className="p-1.5 rounded hover:bg-white/10">
            {playing
              ? <Pause size={16} style={{ color: C.text }} />
              : <Play size={16} style={{ color: C.text }} />}
          </button>
          <button onClick={() => seekRelative(5)} className="p-1 rounded hover:bg-white/10">
            <SkipForward size={14} style={{ color: C.t2 }} />
          </button>
          <span className="text-[11px] ml-1 tabular-nums" style={{ color: C.t2 }}>
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setMuted((v) => !v)} className="p-1 rounded hover:bg-white/10">
            {muted
              ? <VolumeX size={14} style={{ color: C.t3 }} />
              : <Volume2 size={14} style={{ color: C.t2 }} />}
          </button>
          <button
            onClick={() => videoRef.current?.requestFullscreen?.()}
            className="p-1 rounded hover:bg-white/10"
          >
            <Maximize2 size={14} style={{ color: C.t2 }} />
          </button>
        </div>
      </div>

      {/* Indicator pills */}
      {markers.length > 0 && (
        <div className="px-3 pb-3 flex flex-wrap gap-1.5">
          {markers.map((m, i) => (
            <button
              key={i}
              onClick={() => seekTo(m.seconds)}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all hover:scale-105"
              style={{
                background: C.s2,
                border: `1px solid ${m.severity === 'HIGH' ? '#FF444440' : C.border}`,
                color: m.severity === 'HIGH' ? '#FF4444' : C.t2,
              }}
            >
              <Clock size={9} />
              {m.timestamp}
              <span className="max-w-[80px] truncate">{m.tell || m.category || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CHAT PANEL (adapted from VideoChat for pitch tipping)
// ═══════════════════════════════════════════════════════════════════════════

function IntelChat({ analysisText, meta, seekToRef }) {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: `**ECHO** — Pitch Tipping Intelligence\n\nI've completed a full MoE analysis of this video. I can answer questions about:\n\n- **Detected tells** — what the pitcher is revealing\n- **Exploitation strategies** — how hitters can use this intel\n- **Biomechanical details** — joint angles, timing, sequencing\n- **Timestamps** — I'll reference exact moments in the video\n\nWhat would you like to know?`,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const suggestions = useMemo(() => [
    'What are the most exploitable tells?',
    'Explain the biomechanical differences between pitches',
    'What should hitters watch for in real-time?',
    'Break down the glove-side tells at each timestamp',
  ], []);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isTyping) return;
    const text = input.trim();
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);

    try {
      const systemPrompt = buildPitchTippingSystemPrompt(analysisText, meta);
      const history = messages.filter((m) => m.role !== 'system').map((m) => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content,
      }));

      const res = await fetch(`${NODE_BACKEND_URL}/api/chat/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'user', content: systemPrompt },
            { role: 'assistant', content: 'Understood. I have the full pitch tipping analysis loaded. Ask me anything about the findings.' },
            ...history,
            { role: 'user', content: text },
          ],
          options: { model: 'gemini-2.5-flash', temperature: 0.6, sport: 'BSB', teamName: 'UNC' },
        }),
      });

      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      const reply = data.content || data.response || data.message || data.text || 'Unable to generate response.';
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: `Analysis chat error: ${err.message}. Try again.`,
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [input, isTyping, messages, analysisText, meta]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  /** Sanitize markdown and make timestamps clickable */
  const renderMessage = useCallback((text) => {
    const html = safeMd(text);
    // Add clickable timestamp spans (data attributes only, handled via delegation)
    return html.replace(
      /\[(\d{1,2}:\d{2})\]/g,
      `<span class="scouting-ts" data-ts="$1" style="color:${CAROLINA_BLUE};cursor:pointer;text-decoration:underline;font-weight:600">[$1]</span>`
    );
  }, []);

  // Event delegation for timestamp clicks in chat
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      const link = e.target.closest('.scouting-ts');
      if (link && seekToRef?.current) {
        const sec = parseTimestamp(link.dataset.ts);
        if (sec != null) seekToRef.current(sec);
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [seekToRef]);

  return (
    <div className="flex flex-col h-full rounded-xl overflow-hidden" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${CAROLINA_BLUE}20` }}>
          <Target size={14} style={{ color: CAROLINA_BLUE }} />
        </div>
        <div>
          <h3 className="text-xs font-semibold" style={{ color: C.text }}>ECHO Intelligence Chat</h3>
          <p className="text-[10px]" style={{ color: C.t3 }}>Ask about the pitch tipping analysis</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role !== 'user' && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${CAROLINA_BLUE}20` }}>
                <Bot size={12} style={{ color: CAROLINA_BLUE }} />
              </div>
            )}
            <div
              className="max-w-[88%] px-3 py-2 rounded-xl text-[13px] leading-relaxed prose prose-sm prose-invert max-w-none [&_p]:mb-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_h3]:text-sm [&_code]:bg-white/10 [&_code]:px-1 [&_code]:rounded"
              style={{
                background: msg.role === 'user' ? CAROLINA_BLUE : C.s2,
                color: msg.role === 'user' ? '#000' : C.text,
                border: msg.role === 'user' ? 'none' : `1px solid ${C.border}`,
              }}
              dangerouslySetInnerHTML={{ __html: renderMessage(msg.content) }}
            />
            {msg.role === 'user' && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: C.s3 }}>
                <User size={12} style={{ color: C.t2 }} />
              </div>
            )}
          </div>
        ))}

        {isTyping && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${CAROLINA_BLUE}20` }}>
              <Bot size={12} style={{ color: CAROLINA_BLUE }} />
            </div>
            <div className="px-3 py-2 rounded-xl" style={{ background: C.s2 }}>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: C.t3, animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: C.t3, animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: C.t3, animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => { setInput(s); inputRef.current?.focus(); }}
              className="px-2 py-1 rounded-md text-[10px] transition-colors hover:bg-zinc-800"
              style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 flex-shrink-0" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about the pitch tipping analysis..."
            className="flex-1 px-3 py-2 rounded-lg text-xs outline-none"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
            disabled={isTyping}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="px-3 py-2 rounded-lg transition-colors disabled:opacity-30"
            style={{ background: CAROLINA_BLUE, color: '#000' }}
          >
            {isTyping ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SUMMARY HEADER
// ═══════════════════════════════════════════════════════════════════════════

function SummaryHeader({ indicators }) {
  const tippingDetected = indicators.length > 0;
  const highSeverity = indicators.filter((i) => i.severity === 'HIGH').length;
  const avgConfidence = indicators.length > 0
    ? indicators.reduce((s, i) => s + (i.confidence || 0.5), 0) / indicators.length
    : 0;

  return (
    <div
      className="rounded-xl p-4 mb-4 flex items-center gap-4 flex-wrap"
      style={{ background: C.s1, border: `1px solid ${C.border}` }}
    >
      <div className="flex items-center gap-2">
        {tippingDetected ? (
          <AlertTriangle size={16} style={{ color: '#FF4444' }} />
        ) : (
          <CheckCircle size={16} style={{ color: '#00CC66' }} />
        )}
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: tippingDetected ? '#FF4444' : '#00CC66' }}>
          {tippingDetected ? 'Tipping Detected' : 'No Significant Tells'}
        </span>
      </div>

      {indicators.length > 0 && (
        <>
          <div className="h-4 w-px" style={{ background: C.border }} />
          <div className="flex items-center gap-3">
            <span className="text-[11px]" style={{ color: C.t2 }}>
              <strong style={{ color: CAROLINA_BLUE }}>{indicators.length}</strong> indicators
            </span>
            {highSeverity > 0 && (
              <span className="text-[11px]" style={{ color: C.t2 }}>
                <strong style={{ color: '#FF4444' }}>{highSeverity}</strong> high severity
              </span>
            )}
            <span className="text-[11px]" style={{ color: C.t2 }}>
              Confidence: <strong style={{ color: CAROLINA_BLUE }}>{(avgConfidence * 100).toFixed(0)}%</strong>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ScoutingIntelOverlay
 * @param {Object} props
 * @param {string} props.videoUrl - Blob URL or GCS public URL for the video
 * @param {string} props.analysisText - Raw analysis text from MoE pipeline
 * @param {Object} props.meta - { filename, playerName, context }
 * @param {'analyzing'|'results'} props.phase - Current phase
 * @param {number} props.progress - Analysis progress 0-100 (analyzing phase)
 * @param {Object|null} props.currentExpert - Currently active expert persona
 * @param {Function} props.onClose - Close the overlay
 */
export default function ScoutingIntelOverlay({
  videoUrl,
  analysisText,
  meta = {},
  phase = 'results',
  progress = 0,
  currentExpert = null,
  onClose,
}) {
  const seekToRef = useRef(null);
  const indicators = useMemo(() => parseIndicators(analysisText), [analysisText]);

  if (phase === 'analyzing') {
    return (
      <div className="rounded-2xl overflow-hidden" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
        <div className="flex justify-between items-center px-5 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2">
            <Target size={16} style={{ color: CAROLINA_BLUE }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>Pitch Tipping Analysis</span>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1 rounded hover:bg-zinc-800 transition-colors">
              <X size={16} style={{ color: C.t3 }} />
            </button>
          )}
        </div>
        <AnalyzingPhase progress={progress} currentExpert={currentExpert} />
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2">
          <Target size={16} style={{ color: CAROLINA_BLUE }} />
          <span className="text-sm font-bold" style={{ color: C.text }}>
            Scouting Intelligence — {meta.filename || 'Video Analysis'}
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors hover:bg-zinc-800"
            style={{ color: C.t2, border: `1px solid ${C.border}` }}
          >
            <X size={12} /> Close
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="px-5 pt-4">
        <SummaryHeader indicators={indicators} />
      </div>

      {/* Split view: video (left) + chat (right) */}
      <div className="flex flex-col lg:flex-row gap-4 p-5 pt-0">
        <div className="lg:w-[55%] flex-shrink-0">
          <VideoPlayer
            videoUrl={videoUrl}
            indicators={indicators}
            onTimestampClick={seekToRef}
          />
        </div>
        <div className="lg:w-[45%] lg:h-[500px] h-[400px]">
          <IntelChat
            analysisText={analysisText}
            meta={meta}
            seekToRef={seekToRef}
          />
        </div>
      </div>
    </div>
  );
}
